import type { ReactNode } from 'react'

import { createContext, useContext, useEffect, useState } from 'react'

import { authApi } from '../services/authApi'
import type { AuthContextValue, User } from '../types/auth.types'

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem('user')
      return stored ? (JSON.parse(stored) as User) : null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(true)

  function persistSession(token: string, authenticatedUser: User): void {
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(authenticatedUser))
    setUser(authenticatedUser)
  }

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      setLoading(false)
      return
    }

    authApi
      .me()
      .then((response) => setUser(response.data))
      .catch(() => {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      })
      .finally(() => setLoading(false))
  }, [])

  async function login(email: string, password: string): Promise<void> {
    const response = await authApi.login(email, password)
    persistSession(response.data.token, response.data.user)
  }

  async function loginWithToken(token: string): Promise<void> {
    localStorage.setItem('token', token)
    try {
      const response = await authApi.me()
      persistSession(token, response.data)
    } catch (err) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      setUser(null)
      throw err
    }
  }

  function logout(): void {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
  }

  return <AuthContext.Provider value={{ user, loading, login, loginWithToken, logout }}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
